// Pure upload-planning logic, deliberately FREE of React / DOM / network so it can be
// unit-tested in isolation (see uploadPlan.test.ts). The portal imports these; keeping
// them here is what lets a Node test exercise the exact code the UI runs.
import type { TreeNode, SlotNode, SectionType } from '../types'

// One row in the upload tray. `file` is retained on uploading rows so a FAILED upload can
// be retried (re-POSTed) — without it the bytes are unreachable once the promise settles.
// `attempt` counts automatic retries (transient network failures self-heal up to a cap).
// `loaded/total/bps` drive the live "23 of 110 MB · 1.2 MB/s · ~2 min left" readout.
export type Job = {
  id: number; name: string; slotPath: string; progress: number
  status: 'uploading' | 'done' | 'error'; error?: string; file?: File
  attempt?: number; retrying?: boolean; loaded?: number; total?: number; bps?: number
}

export const TYPE_LABEL: Record<SectionType, string> = { photo: 'Photo', video: 'Video', doc: 'Document', media: 'Photos & Videos', any: 'Any file' }
// Never-blank label: an unknown/missing type (e.g. a STALE cached bundle viewing a newer
// type) falls back to "Files" instead of an empty pill.
export const typeLabel = (t: string | undefined): string => (t && TYPE_LABEL[t as SectionType]) || 'Files'

// Find a slot node by its full path ("A/B/Slot") in the LIVE tree — mirrors the server's
// findSlot. Lets upload capacity be gated on the FRESHEST uploaded count, not a stale
// render snapshot (the stale snapshot was the cross-slot count-gate race). Returns null
// for the answer-image pseudo-path ("Questions/<id>"), which isn't part of the tree.
export function findSlotNode(tree: TreeNode[], path: string): SlotNode | null {
  const segs = path.split('/').filter(Boolean)
  let nodes = tree
  for (let i = 0; i < segs.length; i++) {
    const node = nodes.find((n) => n.name === segs[i])
    if (!node) return null
    if (i === segs.length - 1) return node.kind === 'slot' ? node : null
    if (node.kind !== 'folder') return null
    nodes = node.children
  }
  return null
}

// PURE upload planner. Given the current jobs, the slot's LIVE uploaded count + cap, and
// the picked files, decide which become 'uploading' jobs (RETAINING their File for
// progress + retry) and which are rejected as "folder full". The destination `slotPath`
// is snapshotted by the CALLER and threaded in, so a file's destination can never drift
// across a re-render (the cross-slot bug). `alloc` hands out monotonic job ids.
export function planUploads(opts: {
  prevJobs: Job[]; uploadedCount: number; slotCap: number; slotPath: string; files: File[]; alloc: () => number
}): { jobs: Job[]; launch: { id: number; file: File }[] } {
  const inflight = opts.prevJobs.filter((j) => j.status === 'uploading' && j.slotPath === opts.slotPath).length
  let free = opts.slotCap - opts.uploadedCount - inflight
  const jobs: Job[] = []
  const launch: { id: number; file: File }[] = []
  for (const file of opts.files) {
    const id = opts.alloc()
    if (free <= 0) { jobs.push({ id, name: file.name, slotPath: opts.slotPath, progress: 1, status: 'error', error: 'This folder is full.' }); continue }
    free--
    jobs.push({ id, name: file.name, slotPath: opts.slotPath, progress: 0, status: 'uploading', file })
    launch.push({ id, file })
  }
  return { jobs, launch }
}
